"use client"

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react"
import {
  getSubdomainFromHostname,
  getTenantHostname,
} from "@edemics/lib/subdomain"
import {
  lookupTenantByHostname,
  lookupTenantBySubdomain,
  type TenantSummary,
} from "@edemics/services/tenantManagementService"

/**
 * R2 — tenant subdomain context. Resolves the active tenant from
 * `window.location.hostname` at boot, then exposes:
 *
 *   • subdomain — string on tenant subdomains, null on apex / dev / reserved.
 *   • tenant    — resolved TenantSummary from the BE lookup. Null when we're
 *                 on apex, still loading, or the BE 404'd.
 *   • loading   — true while the lookup is in flight (never true on apex).
 *   • error     — string on failed lookup (unknown / disabled tenant); the
 *                 consumer decides whether to show the "tenant not found"
 *                 page or fall through to apex mode.
 *
 * Graceful degradation is the whole point of the shape. R2 lands ahead of
 * R1 (the BE endpoint) — if the lookup 404s, tenant stays null and the app
 * still boots. The apex-login flow inside tenant-app doesn't depend on
 * tenant being populated; it reads subdomain directly.
 *
 * JWT-across-hosts (R2 §4b): after apex-login, LoginPage redirects to the
 * tenant subdomain with `#token=<jwt>&user=<b64>` in the URL fragment.
 * TenantProvider's boot hook hydrates that into sessionStorage before the
 * AuthProvider's own restore runs, so a fresh login on apex → subdomain
 * lands with the session already restored. This provider runs INSIDE
 * AuthProvider so we can't call useAuth here — we write directly to
 * sessionStorage, matching auth-provider's own storage contract.
 */
export interface TenantContextValue {
  /**
   * Slug for edemics.com hosts (`acme.edemics.com` → `"acme"`); `null`
   * on apex, reserved subdomains, dev, or a BYO custom-domain host.
   */
  subdomain: string | null
  /**
   * Wave S (2026-09-10) — full tenant hostname when the current host
   * is a tenant surface (either `<slug>.edemics.com` OR a live BYO
   * custom domain). `null` on apex / reserved / dev. Consumers that
   * only need to know "am I on a tenant surface" should read this
   * rather than `subdomain` so custom-domain tenants aren't excluded.
   */
  hostname: string | null
  tenant: TenantSummary | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

const TenantContext = createContext<TenantContextValue | undefined>(undefined)

/**
 * Read the URL fragment for a `#token=…` handoff from an apex login and
 * copy it into sessionStorage. Wipes the fragment so a back-nav doesn't
 * re-trigger, and so the token doesn't leak to any downstream consumer
 * that inspects the URL.
 *
 * Exported so a bootstrap script or non-React entry can also drain it if
 * needed; the provider calls this synchronously in a useEffect on first
 * mount, before triggering the tenant lookup or letting AuthProvider run
 * its own restore.
 */
function hydrateSessionFromUrlFragment(): void {
  if (typeof window === "undefined") return
  const rawHash = window.location.hash
  if (!rawHash || !rawHash.startsWith("#")) return
  // Fragment format:  #token=<jwt>&user=<base64-json>&role=<active-role>
  // Falls through cleanly if any piece is missing — we only lift what's
  // there and never crash on malformed input.
  const params = new URLSearchParams(rawHash.slice(1))
  const token = params.get("token")
  if (!token) return
  try {
    sessionStorage.setItem("token", token)
    const encodedUser = params.get("user")
    if (encodedUser) {
      try {
        // Handoff payload is base64-encoded JSON so it survives URL
        // encoding without a fragile query-string of dozens of fields.
        const decoded = atob(encodedUser)
        // Round-trip through parse+stringify to sanity-check the payload
        // before we hand it to AuthProvider on next restore.
        const parsed = JSON.parse(decoded)
        sessionStorage.setItem("user", JSON.stringify(parsed))
        if (parsed?.role) sessionStorage.setItem("userRole", String(parsed.role))
      } catch {
        // Malformed user payload — leave the token so /auth/me can rehydrate
        // via the AuthProvider's own restore path, which is the safer fallback.
      }
    }
  } catch {
    // Storage blocked (private browsing quota, etc.) — the user will just
    // land on /login and re-authenticate. Not fatal.
    return
  }
  // Strip the fragment so a refresh / back-nav doesn't re-consume it and
  // so the token doesn't sit in the address bar. history.replaceState is
  // synchronous and doesn't trigger a route change.
  try {
    const cleanUrl = window.location.pathname + window.location.search
    window.history.replaceState(null, "", cleanUrl)
  } catch {
    // If replaceState is unavailable for some exotic reason, the token is
    // already in storage — the visible fragment is cosmetic damage only.
  }
}

export function TenantProvider({ children }: { children: ReactNode }) {
  // Fragment-token handoff runs BEFORE anything else. Sync, one-shot,
  // module-load timing. useState initializer is the earliest legal spot
  // in a React tree — no consumer downstream has had a chance to look at
  // sessionStorage yet at this point.
  useState(() => {
    hydrateSessionFromUrlFragment()
    return null
  })

  const subdomain = useMemo<string | null>(() => {
    if (typeof window === "undefined") return null
    return getSubdomainFromHostname(window.location.hostname)
  }, [])

  // Wave S (2026-09-10) — full tenant hostname. Non-null on
  // `<slug>.edemics.com` AND on BYO custom-domain hosts; null on
  // apex / reserved / dev.
  const hostname = useMemo<string | null>(() => {
    if (typeof window === "undefined") return null
    return getTenantHostname(window.location.hostname)
  }, [])

  const [tenant, setTenant] = useState<TenantSummary | null>(null)
  const [loading, setLoading] = useState<boolean>(hostname !== null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    // Nothing to resolve on apex / reserved / dev — every route runs
    // as-is.
    if (!hostname) {
      setTenant(null)
      setLoading(false)
      setError(null)
      return
    }
    setLoading(true)
    setError(null)
    try {
      // Wave S — prefer the hostname endpoint. Falls back to
      // subdomain-based lookup for edemics.com hosts if the newer
      // endpoint 404s (which would only happen against a stale BE
      // that hasn't shipped Wave S yet).
      let resolved: TenantSummary
      try {
        resolved = await lookupTenantByHostname(hostname)
      } catch (inner: any) {
        if (inner?.response?.status === 404 && subdomain) {
          // Legacy fallback path — a subdomain host on a pre-Wave-S
          // BE returns 404 for /lookup-by-hostname because the route
          // doesn't exist yet. Try the older endpoint.
          resolved = await lookupTenantBySubdomain(subdomain)
        } else {
          throw inner
        }
      }
      setTenant(resolved)
    } catch (e: any) {
      const status = e?.response?.status
      if (status === 404) {
        setTenant(null)
        setError(`Tenant "${hostname}" not found`)
      } else {
        setTenant(null)
        setError(
          e?.response?.data?.message ||
            e?.message ||
            `Failed to resolve tenant "${hostname}"`,
        )
      }
    } finally {
      setLoading(false)
    }
  }, [hostname, subdomain])

  useEffect(() => {
    void load()
  }, [load])

  const value = useMemo<TenantContextValue>(
    () => ({ subdomain, hostname, tenant, loading, error, refetch: load }),
    [subdomain, hostname, tenant, loading, error, load],
  )

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
}

export function useTenant(): TenantContextValue {
  const ctx = useContext(TenantContext)
  if (ctx === undefined) {
    throw new Error("useTenant must be used within a TenantProvider")
  }
  return ctx
}
